<template>
  <v-flex
    v-if="getNeedsVolunteersQuestions"
    id="volunteers"
    class="question"
  >
    <h2 class="header required mt-0 mb-0">
      {{ getNeedsVolunteersQuestions?.label }}
    </h2>
    <v-layout
      class="mt-3"
      :wrap="$vuetify.breakpoint.smAndDown"
    >
      <v-flex
        lg6
        sm12
        class="text-dark"
      >
        <p class="fw-600 f-2">
          {{ $t('label.main_volunteer') }}
        </p>
        <div v-if="family?.primary_volunteer">
          <p class="f-1 mb-1">
            {{ family.primary_volunteer?.full_name }}
          </p>
          <p class="mb-1">
            <BaseEmail :email="family.primary_volunteer?.email" />
          </p>
          <v-layout>
            <v-flex
              md4
              class="none-growth"
            >
              {{ $t('label.tel') }}
            </v-flex>
            <v-flex
              v-if="family?.primary_volunteer?.phone"
              md8
            >
              <p class="mb-1">
                <PhoneNumber :number="family.primary_volunteer.phone" />
              </p>
            </v-flex>
          </v-layout>
        </div>
      </v-flex>
      <v-flex
        lg6
        sm12
        class="text-dark"
        :class="[{ 'mt-3': $vuetify.breakpoint.smAndDown }]"
      >
        <p class="fw-600 f-2">
          {{ $t('label.sidekick') }}
        </p>
        <div v-if="family?.secondary_volunteer">
          <p class="f-1 mb-1">
            {{ family.secondary_volunteer?.full_name }}
          </p>
          <p class="mb-1">
            <BaseEmail :email="family.secondary_volunteer?.email" />
          </p>
          <v-layout>
            <v-flex
              md4
              class="none-growth"
            >
              {{ $t('label.tel') }}
            </v-flex>
            <v-flex
              v-if="family?.secondary_volunteer?.phone"
              md8
            >
              <p class="mb-1">
                <PhoneNumber :number="family.secondary_volunteer.phone" />
              </p>
            </v-flex>
          </v-layout>
        </div>
      </v-flex>
    </v-layout>
    <p
      class="ml-2 mt-3 mb-0"
      :class="{ required: getNeedsVolunteersQuestions?.date?.required }"
    >
      {{ getNeedsVolunteersQuestions?.date?.label }}
    </p>
    <v-layout>
      <v-flex
        xs12
        sm6
        md4
        lg3
      >
        <v-menu
          ref="dateMenu"
          v-model="menu"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="date"
          lazy
          transition="scale-transition"
          offset-y
        >
          <template slot="activator">
            <v-text-field
              v-model="date"
              v-validate="{ required: getNeedsVolunteersQuestions?.date?.required }"
              readonly
              placeholder="dd.mm.rrrr"
              :error-messages="errors.collect('volunteers_interview_date')"
              :data-vv-as="$t('sheet.sheetOne.meeting.volunteers.date')"
              :data-vv-name="'volunteers_interview_date'"
              :required="getNeedsVolunteersQuestions?.date?.required"
              :disabled="readOnly"
            >
              <template slot="append-outer">
                <BaseIcon>
                  <CalendarIcon />
                </BaseIcon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="date"
            no-title
            scrollable
            locale="pl"
            first-day-of-week="1"
            :max="maxDate"
            :disabled="readOnly || !isCurrentEdition"
            @input="$refs.dateMenu.save(date)"
          >
            <v-btn
              flat
              color="primary"
              @click="menu = false"
            >
              {{ $t('buttons.cancel') }}
            </v-btn>
            <v-btn
              flat
              color="primary"
              @click="$refs?.dateMenu?.save(date)"
            >
              {{ $t('buttons.ok') }}
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-flex>
    </v-layout>
    <v-divider class="my-4" />
    <v-layout
      v-if="getNeedsVolunteerStatementQuestions"
      mt-3
    >
      <v-flex xs12>
        <h2 class="header required mt-0 mb-0">
          {{ getNeedsVolunteerStatementQuestions?.label }}
        </h2>
        <div>
          <v-checkbox
            v-if="getNeedsVolunteerStatementQuestions"
            v-model="volunteerStatement"
            v-validate="{ required: getNeedsVolunteerStatementQuestions?.required }"
            :false-value="null"
            :true-value="true"
            :error-messages="errors.collect('volunteerStatement')"
            :data-vv-as="' '"
            data-vv-name="volunteerStatement"
            :required="getNeedsVolunteerStatementQuestions?.required"
            :disabled="readOnly"
          >
            <template slot="label">
              <div>
                <p
                  v-for="(text, index) in getNeedsVolunteerStatementQuestions?.text"
                  :key="index"
                  class="mb-1"
                  :class="{ required: index === 1 }"
                  v-html="text"
                />
              </div>
            </template>
          </v-checkbox>
        </div>
      </v-flex>
    </v-layout>
    <v-divider class="my-4" />
  </v-flex>
</template>

<script>
import BaseIcon from '@/icons/BaseIcon';
import CalendarIcon from '@/icons/CalendarIcon';

import filters from '@/constants/filters';
import validationRules from '@/constants/validationRules';

import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';
import { formatDate } from '@/utils/formatDate';

import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';

import BaseEmail from '@/components/widgets/BaseEmail.vue';
import PhoneNumber from '@/components/widgets/PhoneNumber.vue';

const { mapFields } = createHelpers({
    getterType: 'sheet/getSheetTwoVolunteersFields',
    mutationType: 'sheet/updateSheetTwoVolunteersFields'
});

export default {
    name: 'SheetTwoVolunteers',
    components: { BaseIcon, CalendarIcon, PhoneNumber, BaseEmail },
    filters: {
        ...filters
    },
    mixins: [sheetValidationRules],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            validationRules,
            menu: false
        };
    },
    computed: {
        ...mapFields(['date', 'volunteerStatement']),
        maxDate () {
            const today = new Date();
            return this.formatDate(today, { useISO: true });
        },
        family () {
            return this.$store.getters['family/getFamily'];
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        },
        ...mapGetters('sheet', ['getNeedsVolunteersQuestions', 'getNeedsVolunteerStatementQuestions'])
    },
    methods: {
        formatDate
    }
};
</script>

<style scoped></style>
